<script setup lang="ts">
import { ref, computed, watch, storeToRefs, navigateTo, useRoute } from '#imports';
import { useMediaQuery, useScrollLock, useWindowSize, watchDebounced } from '@vueuse/core';
import type { ItemList } from '@/shared/types';

import searchService from '@/entities/Search/api';
import { useDictionariesStore } from '@/entities/Dictionary/store/dictionaries';
import { useUserDialogStore } from '@/entities/User/store/dialog';
import { useUserAuthStore } from '@/entities/User/store/auth';
import PopupUserProfile from '@/entities/User/components/popup-user-profile.vue';
import { useArticlesStore } from '@/entities/Article/store';

import { BREAKPOINT } from '@/shared/constant';

import IconSearch from '@/assets/icons/icon-search.svg';
import IconBurger from '@/assets/icons/icon-burger.svg';
import IconUser from '@/assets/icons/icon-user.svg';

/** navigation (mobile, desktop) */
const $html = document?.documentElement;
const isLocked = useScrollLock($html);
const isOpenMobileMenu = ref(false);
const isOpenMobileSearch = ref(false);
const isMobile = ref(false);

const searchField = ref<null | { field: { input: HTMLInputElement | HTMLTextAreaElement }}>(null);

watch(isOpenMobileSearch, (val) => {
	isLocked.value = val;

	setTimeout(() => {
		val && searchField.value?.field?.input?.focus();
	}, 500);
});

watch(isOpenMobileMenu, (val) => {
	isLocked.value = val;
}, {
	immediate: true,
});

const isBigScreen = useMediaQuery('(min-width: 1024px)');

const handleToggleSearch = () => {
	if (!isMobile.value) return;

	isOpenMobileMenu.value = false;
	isOpenMobileSearch.value = !isOpenMobileSearch.value;
};

const handleToggleMenu = () => {
	if (isBigScreen.value) return;

	isOpenMobileSearch.value = false;
	isOpenMobileMenu.value = !isOpenMobileMenu.value;
};

const handleCloseMenu = () => {
	if (isBigScreen.value) return;

	isOpenMobileMenu.value = false;
};

const handleClose = () => {
	if (isBigScreen.value) return;

	isOpenMobileMenu.value = false;
	isOpenMobileSearch.value = false;
};

const { width } = useWindowSize();

watch(width, (val) => {
	if (val > BREAKPOINT.lg) {
		isOpenMobileMenu.value = false;
		isOpenMobileSearch.value = false;
	}
}, {
	immediate: true,
});

watchDebounced(width, (val) => {
	isMobile.value = val < BREAKPOINT.lg;
}, {
	debounce: 500,
	immediate: true,
});

const computedClass = computed(() => ({
	'header': true,
	'header--mobile': isMobile.value,
}));

/** Profile */
const dictionariesStore = useDictionariesStore();
const { resetSelectDictionaryId } = dictionariesStore;

const userAuthStore = useUserAuthStore();
const { isAuth } = storeToRefs(userAuthStore);

const userDialogStore = useUserDialogStore();
const { showSpecialUserDialog } = userDialogStore;

const isOpenUserProfile = ref(false);

const handleToggleProfile = () => {
	if (isAuth.value) {
		isOpenUserProfile.value = true;
	} else {
		showSpecialUserDialog('sign');
	}
};

/** Search */
const articlesStore = useArticlesStore();
const { getArticleById } = articlesStore;

const handleSearchPick = async (item: ItemList) => {
	const article = await getArticleById(item.id as number);

	navigateTo(`/dictionary/${article.slug_path}`);
};

const route = useRoute();
const isSearchPage = computed(() => route.fullPath.includes('/search'));

/** Overlay */
const isOverlay = computed(() => {
	return (isOpenMobileSearch.value || isOpenMobileMenu.value || isOpenUserProfile.value) && !isBigScreen.value;
});
</script>

<template>
	<header id="header" :class="computedClass">
		<div class="header__content">
			<div class="header__container container">
				<nuxt-link
					to="/"
					class="header__logo"
					@click="[handleClose(), resetSelectDictionaryId()]"
				>
					<img
						src="/images/logo/logo.svg"
						alt="Медина логотип"
						class="header__logo-image"
					>
				</nuxt-link>

				<client-only>
					<teleport to="#header" :disabled="isBigScreen">
						<transition name="slide">
							<div
								v-if="isOpenMobileSearch || isBigScreen"
								class="header__search"
							>
								<v-input-search
									ref="searchField"
									is-header
									name="search"
									placeholder="Введите статью, которую хотите найти..."
									:variations="['absolute']"
									:mapper-options="['id', 'title']"
									:fetch-func="searchService.fetchArticles"
									@pick="handleSearchPick"
									@on-navigate="handleToggleSearch"
								/>
							</div>
						</transition>
					</teleport>
				</client-only>

				<client-only>
					<teleport to="#header" :disabled="isBigScreen">
						<transition name="slide">
							<nav
								v-if="isOpenMobileMenu || isBigScreen"
								class="header__navigation"
							>
								<ul class="header__navigation-list">
									<li class="header__navigation-item">
										<nuxt-link
											to="/gallery"
											class="header__navigation-link"
											exact-active-class="header__navigation-link--active"
											@click="handleCloseMenu"
										>
											Галерея
										</nuxt-link>
									</li>

									<li class="header__navigation-item">
										<nuxt-link
											to="/about"
											class="header__navigation-link"
											exact-active-class="header__navigation-link--active"
											@click="handleCloseMenu"
										>
											О проекте
										</nuxt-link>
									</li>

									<li class="header__navigation-item">
										<nuxt-link
											to="/news"
											class="header__navigation-link"
											exact-active-class="header__navigation-link--active"
											@click="handleCloseMenu"
										>
											Новости
										</nuxt-link>
									</li>

									<li class="header__navigation-item">
										<nuxt-link
											to="/contacts"
											class="header__navigation-link"
											exact-active-class="header__navigation-link--active"
											@click="handleCloseMenu"
										>
											Контакты
										</nuxt-link>
									</li>

									<li v-if="isBigScreen" class="header__navigation-item header__navigation-item--profile">
										<button class="button" @click="handleToggleProfile">
											<icon-user class="icon icon--mini" />
										</button>

										<popup-user-profile
											v-model="isOpenUserProfile"
											class="header__profile"
										/>
									</li>
								</ul>
							</nav>
						</transition>
					</teleport>

					<ul v-if="!isBigScreen" class="header__action">
						<li v-if="!isSearchPage" class="header__action-item">
							<button
								class="header__action-button button button--icon"
								@click="handleToggleSearch"
							>
								<icon-search class="header__action-icon icon" />
							</button>
						</li>
						<li class="header__action-item header__action-item--profile">
							<button
								class="header__action-button button button--icon"
								@click="handleToggleProfile"
							>
								<icon-user class="header__action-icon icon" />
							</button>

							<popup-user-profile
								v-model="isOpenUserProfile"
								class="header__profile"
							/>
						</li>
						<li class="header__action-item">
							<button
								class="header__action-button button button--icon"
								@click="handleToggleMenu"
							>
								<icon-burger class="header__action-icon icon" />
							</button>
						</li>
					</ul>
				</client-only>
			</div>
		</div>

		<transition name="overlay">
			<div
				v-if="isOverlay"
				class="overlay"
				@click="handleClose"
			/>
		</transition>
	</header>
</template>

<style lang="scss" scoped>
.header {
	$gap-header: 80px;
	$gap-header-tablet: 97px;
	$gap-search: $gap-header + 12px;
	$gap-search-tablet: $gap-header-tablet + 12px;

	position: relative;

	&--mobile {
		.slide-enter-active,
		.slide-leave-active {
			transition: transform $transition-duration $transition-function;

			@media #{$screen-desktop} {
				transition: opacity $transition-duration $transition-function;
			}
		}

		.slide-enter-from,
		.slide-leave-to {
			transform: translateY(-100%);

			@media #{$screen-laptop} {
				transform: translateY(0);

				opacity: 0;
			}
		}

		.slide-enter-to,
		.slide-leave-from {
			transform: translateY(0);

			@media #{$screen-laptop} {
				transform: initial;
			}
		}
	}

	&__content {
		position: relative;
		z-index: 3;

		padding: rem($gap-small) 0 rem(12px);

		background-color: $white;

		@media #{$screen-tablet} {
			padding: rem(20px) 0;
		}
	}

	&__container {
		display: flex;
		align-items: center;
		justify-content: space-between;

		@media #{$screen-laptop} {
			justify-content: flex-start;
		}
	}

	&__logo {
		display: block;

		@media #{$screen-tablet} {
			margin-right: rem(52px);
		}

		&-image {
			max-width: 100%;
			height: rem(52px);

			@media #{$screen-laptop} {
				height: rem(105px);
			}
		}
	}

	&__search {
		position: absolute;
		z-index: 2;
		top: rem($gap-search);
		right: rem(22px);
		left: rem(22px);

		flex-grow: 1;

		@media print and (max-width: 1023px) {
			display: none;
		}

		@media #{$screen-tablet} {
			top: rem($gap-search-tablet);
		}

		@media #{$screen-laptop} {
			position: initial;
			z-index: initial;
			top: initial;
			right: initial;
			left: initial;

			max-width: rem(640px);
			margin-right: rem(52px);
		}
	}

	&__navigation {
		position: absolute;
		z-index: 2;
		top: rem($gap-header);
		left: 0;

		width: 100%;
		padding: rem($gap-small) rem(12px) rem($gap-medium);

		background-color: $wild-sand;
		box-shadow: 0 rem(4px) rem(4px) 0 #00000040;

		@media print and (max-width: 1023px) {
			display: none;
		}

		@media #{$screen-tablet} {
			top: rem($gap-header-tablet);
		}

		@media #{$screen-laptop} {
			position: initial;
			z-index: initial;
			top: initial;
			left: initial;

			width: auto;
			margin-left: auto;
			padding: 0;

			background-color: transparent;
			box-shadow: none;
		}

		&-list {
			@media #{$screen-laptop} {
				display: flex;
				align-items: center;
			}
		}

		&-item {
			text-align: right;

			@media #{$screen-laptop} {
				text-align: left;
			}

			& + & {
				margin-top: rem($gap-average);

				@media #{$screen-laptop} {
					margin-top: 0;
					margin-left: rem($gap);
				}
			}

			&--profile {
				position: relative;
			}
		}

		&-link {
			@include text-medium;

			transition: color $transition-function $transition-duration;
			text-decoration: none;
			text-underline-offset: rem(4px);

			color: $black;

			@media #{$screen-tablet} {
				@include text-base;
			}

			@media (hover: hover) {
				&:hover {
					text-decoration: underline;
				}
			}

			&--active {
				text-decoration: underline;
			}
		}
	}

	&__action {
		display: flex;

		&-item {
			& + & {
				margin-left: rem($gap-small);
			}

			&--profile {
				position: relative;
			}
		}

		&-icon {
			$size: 21px;

			width: rem($size);
			height: rem($size);

			color: $black;
		}
	}

	&__profile {
		position: fixed;
		right: 0;

		@media #{$screen-laptop} {
			position: absolute;
		}
	}
}
</style>
